import {
  IDocumentLineageRoot,
  IDocumentRelationshipEntry,
  IDocumentUnscoped,
} from '../../types/my-types';

export interface DuplicateLineage {
  parent: IDocumentRelationshipEntry | null;
  children: IDocumentRelationshipEntry[];
  isDuplicateChild: boolean;
  showDuplicates: boolean;
}

// Derive a document's duplicate lineage from the relationship fields. Shared by
// the manifest row, the detail page, and the promote-to-parent gate so they stay
// in sync (relationship-based, not hash/status-based).
export const getDuplicateLineage = (doc: IDocumentUnscoped): DuplicateLineage => {
  const parent = doc.relationships?.duplicate?.parent ?? null;
  const children = doc.relationships?.duplicate?.children ?? [];
  const isDuplicateChild = parent != null;
  return { parent, children, isDuplicateChild, showDuplicates: isDuplicateChild || children.length > 0 };
};

export interface LineageAnchor {
  /** DocumentData id to filter by. NEVER a Document id — see below. */
  anchorDocumentDataId: string;
  /** This row is itself the origin of its tree. */
  isOrigin: boolean;
  /** The origin's entry; null when this row IS the origin. */
  root: IDocumentLineageRoot | null;
  /** The origin lives in another data pool, so a pool-scoped list cannot show it. */
  isCrossPool: boolean;
}

// Lineage labels only. `duplicate` is deliberately absent: a duplicate is a copy, not an
// ancestor, and promote-to-parent reverses duplicate edges. This mirrors LINEAGE_LABELS
// in the API's packager/lineage.py, which is what actually resolves a root.
const LINEAGE_KEYS = ['split', 'translated', 'reintroduced'] as const;

/**
 * Where this document sits in a lineage tree, or null if it is in none.
 *
 * Two branches on purpose. A row with a root is a descendant and anchors on that root.
 * A row WITHOUT a root may still be the tree's origin — the API returns
 * `root_document_data: null` for an origin exactly as it does for an unrelated document
 * — so origin-ness is read from the lineage relationship keys instead. Skip that branch
 * and the bundle an operator opens first is the one row with no affordance, while the
 * filter's own result set does include it.
 *
 * Reads the three lineage keys, never `has_children`: that flag is true for a parent in
 * ANY relationship type, and reading it as type-specific is what disabled Split on the
 * canonical document of every duplicate set (GP-1100). Same reasoning as
 * `splitEligibility.isDocumentAlreadySplit`.
 */
export const getLineageAnchor = (doc: IDocumentUnscoped): LineageAnchor | null => {
  const root = doc.root_document_data ?? null;
  if (root) {
    return {
      anchorDocumentDataId: root.document_data_id,
      isOrigin: false,
      root,
      isCrossPool: root.data_pool_id !== doc.data_pool_id,
    };
  }
  const hasLineageChildren = LINEAGE_KEYS.some(
    (k) => (doc.relationships?.[k]?.children?.length ?? 0) > 0
  );
  // document_data_id is nullable; the row already guards it this way before
  // PromoteToParentAction. Without a DocumentData there is nothing to anchor on.
  if (hasLineageChildren && doc.document_data_id) {
    return {
      anchorDocumentDataId: doc.document_data_id,
      isOrigin: true,
      root: null,
      isCrossPool: false,
    };
  }
  return null;
};

export const addDocumentQueryParams = (
  labelContains: string | undefined,
  hasBeenSent: string | undefined,
  isSupported: string | undefined,
  formatId: string | undefined,
  createdAfter: string | undefined,
  createdBefore: string | undefined,
  includeName: string | undefined,
  excludeName: string | undefined,
  includeId: string | undefined,
  excludeId: string | undefined,
  includeLanguage: string | undefined,
  excludeLanguage: string | undefined,
  includeDuplicateKey: string | undefined,
  excludeDuplicateKey: string | undefined,
  includeDocumentDataStatus?: string | undefined,
  excludeDocumentDataStatus?: string | undefined
) => {
  return {
    ...(labelContains && { ilabel__contains: labelContains }),
    ...(hasBeenSent && { has_verified_extraction: hasBeenSent === 'yes' ? 'true' : 'false' }),
    ...(isSupported && { is_supported: isSupported === 'yes' ? 'true' : 'false' }),
    ...(formatId && { format_id: formatId }),
    ...(createdAfter && { created_after: createdAfter }),
    ...(createdBefore && { created_before: createdBefore }),
    ...(includeName && { name: includeName }),
    ...(excludeName && { exclude_name: excludeName }),
    ...(includeId && { id: includeId }),
    ...(excludeId && { exclude_id: excludeId }),
    ...(includeLanguage && { language: includeLanguage }),
    ...(excludeLanguage && { exclude_language: excludeLanguage }),
    ...(includeDuplicateKey && { duplicate_key: includeDuplicateKey }),
    ...(excludeDuplicateKey && { exclude_duplicate_key: excludeDuplicateKey }),
    ...(includeDocumentDataStatus && { document_data_status: includeDocumentDataStatus }),
    ...(excludeDocumentDataStatus && { exclude_document_data_status: excludeDocumentDataStatus }),
  };
};

/**
 * The manifest's graph filters: each selects a set of related documents, and the API ANDs
 * them, so two at once collapses the list. `id`/`selectedOnly` narrow to an explicit set of
 * document ids, which ANDs the same way — a drill-down launched from a selected-only view
 * would return just the row that was clicked instead of the set it belongs to.
 *
 * One place, so a third graph filter cannot be half-wired into some call sites only.
 */
export const IN_LINEAGE_WITH = 'in_lineage_with';
export const IN_RELATIONSHIP_SET_WITH = 'in_relationship_set_with';
const DUPLICATE_KEY = 'duplicate_key';

const GRAPH_FILTER_KEYS = [IN_RELATIONSHIP_SET_WITH, IN_LINEAGE_WITH, DUPLICATE_KEY];

export const applyExclusiveGraphFilter = (
  searchParams: URLSearchParams,
  key: string,
  value: string
) => {
  searchParams.set('page', '0');
  GRAPH_FILTER_KEYS.forEach((k) => searchParams.delete(k));
  searchParams.delete('id');
  searchParams.delete('selectedOnly');
  searchParams.set(key, value);
};

export const syncSelectedDocumentParams = (
  searchParams: URLSearchParams,
  selectedIds: string[]
) => {
  const isSelectedOnlyView = searchParams.get('selectedOnly') === 'true';

  if (selectedIds.length === 0) {
    searchParams.delete('selectedIds');
    searchParams.delete('selectedOnly');
    if (isSelectedOnlyView) {
      searchParams.delete('id');
    }
    return;
  }

  const selectedIdCsv = selectedIds.join(',');
  searchParams.set('selectedIds', selectedIdCsv);

  if (isSelectedOnlyView) {
    searchParams.set('id', selectedIdCsv);
  }
};
